import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { message, ConfigProvider, theme } from 'antd';
import { socketService } from '../services/socketService';
import { useNavigate } from 'react-router-dom';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

// Preload notification sound
const msgSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');

export const ChatProvider = ({ children }) => {
    const navigate = useNavigate();
    
    const [stats, setStats] = useState({});
    const [totalOnline, setTotalOnline] = useState(0);
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [messages, setMessages] = useState([]);
    const [partnerTyping, setPartnerTyping] = useState(false);
    const [searching, setSearching] = useState(false);
    const [partnerLeft, setPartnerLeft] = useState(false);
    const [darkMode, setDarkMode] = useState(localStorage.getItem('chat_dark') === 'true');
    const [userPrefs, setUserPrefs] = useState(null);
    const [connected, setConnected] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const originalTitle = useRef(document.title);

    const toggleDarkMode = () => {
        const newVal = !darkMode;
        setDarkMode(newVal);
        localStorage.setItem('chat_dark', newVal);
    };

    const playSound = useCallback(() => {
        msgSound.play().catch(() => {});
    }, []);

    // Handle Title Notifications
    useEffect(() => {
        if (unreadCount > 0 && document.hidden) {
            document.title = `(${unreadCount}) Նոր հաղորդագրություն`;
        } else {
            document.title = originalTitle.current;
            if (!document.hidden) setUnreadCount(0);
        }
    }, [unreadCount]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setUnreadCount(0);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
        socketService.onConnect(() => setConnected(true));
        socketService.onDisconnect(() => setConnected(false));

        socketService.onStatsUpdate((data) => {
            setStats(data.stats);
            setTotalOnline(data.total);
        });

        socketService.onMatched((data) => {
            const myId = socketService.id;
            const partner = data.u1.id === myId ? data.u2 : data.u1;
            
            setPartnerInfo(partner);
            setPartnerLeft(false);
            
            const genderText = partner.gender === 'male' ? 'Արական' : 'Իգական';
            const systemMsg = {
                id: 'sys_' + Date.now(),
                type: 'system',
                text: `Դուք միացաք ${partner.age} տարեկան ${genderText} զրուցակցի հետ`
            };
            setMessages([systemMsg]);
            setSearching(false);
            message.success("Զրուցակիցը գտնվեց:");
            navigate('/chat');
        });

        socketService.onSearching(() => {
            setSearching(true);
            setPartnerLeft(false);
            navigate('/search');
        });

        socketService.onMessage((msg) => {
            const isOwn = msg.from === socketService.id;
            const type = isOwn ? 'own' : 'partner';
            
            setMessages(prev => [...prev, { ...msg, type }]);
            setPartnerTyping(false);
            
            if (!isOwn) {
                playSound();
                socketService.sendReadReceipt(msg.id);
                if (document.hidden) {
                    setUnreadCount(prev => prev + 1);
                }
            }
        });

        socketService.onPartnerRead((msgId) => {
            setMessages(prev => prev.map(m => m.id === msgId ? { ...m, read: true } : m));
        });

        socketService.onTyping((typing) => {
            setPartnerTyping(typing);
        });

        socketService.onPartnerLeft(() => {
            setPartnerLeft(true);
            setMessages(prev => [...prev, { 
                id: 'sys_' + Date.now(), 
                type: 'system', 
                text: 'Զրուցակիցը լքեց չատը' 
            }]);
            message.warning("Զրուցակիցը լքեց չատը");
        });

        socketService.onReportSuccess(() => {
            message.success("Օգտատերը հաջողությամբ զեկուցվել է");
        });

        return () => socketService.disconnect();
    }, [navigate, playSound]);

    const resetChat = () => {
        setPartnerInfo(null);
        setMessages([]);
        setPartnerTyping(false);
        setSearching(false);
        setPartnerLeft(false);
        navigate('/');
    };

    const joinPool = (prefs) => {
        setUserPrefs(prefs);
        setPartnerLeft(false);
        socketService.joinPool(prefs);
    };

    const leave = () => {
        socketService.leave();
        resetChat();
    };

    const nextPartner = () => {
        if (userPrefs) {
            setMessages([]);
            setPartnerInfo(null);
            setPartnerTyping(false);
            setPartnerLeft(false);
            socketService.nextPartner(userPrefs);
        }
    };

    const sendMessage = (text) => {
        socketService.sendMessage(text);
    };

    const sendTyping = (isTyping) => {
        socketService.sendTyping(isTyping);
    };

    const reportPartner = (reason) => {
        socketService.report(reason);
    };

    return (
        <ChatContext.Provider value={{
            stats, totalOnline, partnerInfo, messages, partnerTyping, searching, partnerLeft,
            darkMode, toggleDarkMode, joinPool, leave, nextPartner, sendMessage, 
            sendTyping, reportPartner, connected, socketId: socketService.id
        }}>
            <ConfigProvider theme={{
                algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                token: {
                    borderRadius: 12,
                    colorPrimary: '#1677ff',
                }
            }}>
                <div style={{ 
                    background: darkMode ? '#141414' : '#f0f2f5', 
                    minHeight: '100vh', 
                    transition: 'all 0.3s'
                }}>
                    {children}
                </div>
            </ConfigProvider>
        </ChatContext.Provider>
    );
};